import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    ButtonProps,
    DialogProps,
    FormControl,
    Grid,
    IconButton,
    IconButtonProps,
    MenuItem,
    Button as MuiButton,
    Dialog as MuiDialog,
    Select,
    styled,
    TextField,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useContext } from "react";
import { ChromePicker } from "react-color";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import {
    mutationCreateCategory,
    mutationUpdateCategory,
} from "src/api/category";
import { Enum_Border_Style } from "src/api/category/enum";
import { LanguageContext } from "src/contexts/language";
import { MapContext } from "src/contexts/map";
import { SettingContext } from "src/contexts/settings";
import { Enum_Geometry_Types } from "src/types";
import { Type_Category } from "src/types/category";
import { pictoOptions } from "src/utils/category";
import { createCategorySchema } from "src/validators/createCategory";

type Type_Props_Create_Category = {
    close: () => void;
};

export type Type_Create_Category_Data = {
    title: string;
    type_id: string;
    parent_id?: string;
    picto: string;
    primary_color: string;
    background_color?: string;
    border_style?: string;
    border_width?: number;
    user_id: string;
};

const Icon = styled(IconButton)<IconButtonProps>(() => ({
    position: "absolute",
    top: 10,
    right: 10,
}));

const Button = styled(MuiButton)<ButtonProps>(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const Dialog = styled(MuiDialog)<DialogProps>(({ theme }) => ({
    height: "100%",
    "& .MuiDialog-paper": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        height: "90%",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            position: "absolute",
            bottom: 0,
            margin: 0,
            width: "100%",
        },
    },
}));

const CreateCategory: React.FC<Type_Props_Create_Category> = ({ close }) => {
    const { t } = useTranslation();
    const { language } = useContext(LanguageContext);
    const { categories, connectedUser } = useContext(MapContext);
    const { category } = useContext(SettingContext);

    const queryClient = useQueryClient();

    const methods = useForm({
        resolver: yupResolver(createCategorySchema(t)),
        defaultValues: {
            title: category?.title ?? "",
            type_id: category?.nature ?? Enum_Geometry_Types.POINT,
            parent_id: category?.parent?.id ?? "",
            picto: category?.picto ?? "",
            primary_color: category?.primary_color ?? "",
            background_color: category?.background_color ?? "",
            border_style: category?.border_style ?? Enum_Border_Style.DASHED,
            border_width: category?.border_width ?? 1,
            user_id: connectedUser?.id.toString(),
        },
    });

    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
    } = methods;

    const type = watch("type_id");

    const { mutateAsync: mutateCreateCategory } = mutationCreateCategory();
    const { mutateAsync: mutateUpdateCategory } = mutationUpdateCategory();

    const onSubmit = async (data: Partial<Type_Create_Category_Data>) => {
        const categoryObject: Omit<Type_Category, "id"> = {
            title: { [language]: data.title ?? "" },
            picto: data.picto ?? "",
            parent_id:
                data.parent_id && data.parent_id !== ""
                    ? parseInt(data.parent_id, 10)
                    : null,
            style: {
                primary_color: data.primary_color ?? "",
                background_color: data.background_color,
                border_width: data.border_width,
                border_style: data.border_style as Enum_Border_Style,
            },
            nature: data.type_id as Enum_Geometry_Types,
            user_id: connectedUser?.id,
        };

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        if (!categoryObject.parent_id) delete categoryObject.parent_id!;

        if (!category) await mutateCreateCategory(categoryObject);
        else
            await mutateUpdateCategory({
                categoryId: category.id,
                body: categoryObject,
            });

        close();
        queryClient.invalidateQueries(["categories"]);
    };

    return (
        <Dialog open onClose={close} fullWidth maxWidth="md">
            <Icon disableRipple onClick={close} size="small">
                <FontAwesomeIcon icon={faTimes} />
            </Icon>

            <Box width="90%">
                <Typography mb={3} variant="h5">
                    {category
                        ? t("category.update_category")
                        : t("category.create_category")}
                </Typography>

                <FormProvider {...methods}>
                    <Grid
                        component="form"
                        container
                        width="100%"
                        spacing={2}
                        onSubmit={handleSubmit(
                            (data) =>
                                void onSubmit(
                                    data as unknown as Type_Create_Category_Data,
                                ),
                        )}
                    >
                        <Grid item xs={6}>
                            <Typography color="text.secondary">
                                {t("category.title")}
                            </Typography>
                            <Controller
                                name="title"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size="small"
                                        error={!!errors.title}
                                        helperText={errors.title?.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Typography color="text.secondary">
                                {t("category.picto")}
                            </Typography>
                            <Controller
                                name="picto"
                                control={control}
                                render={({ field }) =>
                                    type === Enum_Geometry_Types.POINT ? (
                                        <FormControl size="small">
                                            <Select {...field}>
                                                {Object.entries(
                                                    pictoOptions,
                                                ).map(([key, icon]) => (
                                                    <MenuItem
                                                        key={key}
                                                        value={key}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={icon}
                                                        />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            placeholder="fa-solid fa-pen-swirl"
                                            size="small"
                                            error={!!errors.picto}
                                            helperText={errors.picto?.message}
                                        />
                                    )
                                }
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Typography color="text.secondary">
                                {t("category.nature")}
                            </Typography>
                            <Controller
                                name="type_id"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth size="small">
                                        <Select {...field}>
                                            {Object.values(
                                                Enum_Geometry_Types,
                                            ).map((value) => (
                                                <MenuItem
                                                    key={value}
                                                    value={value}
                                                >
                                                    {value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <Typography color="text.secondary">
                                {t("category.parent")}
                            </Typography>
                            <Controller
                                name="parent_id"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth size="small">
                                        <Select {...field}>
                                            {categories
                                                .filter(
                                                    (category) =>
                                                        category.parent_id ===
                                                        null,
                                                )
                                                .map(
                                                    (
                                                        category: Type_Category,
                                                    ) => (
                                                        <MenuItem
                                                            key={category.id}
                                                            value={category.id}
                                                        >
                                                            {
                                                                category.title[
                                                                    language
                                                                ]
                                                            }
                                                        </MenuItem>
                                                    ),
                                                )}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        {type !== Enum_Geometry_Types.POINT && (
                            <>
                                <Grid item xs={6}>
                                    <Typography color="text.secondary">
                                        {t("category.border_width")}
                                    </Typography>
                                    <Controller
                                        name="border_width"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                size="small"
                                                type="number"
                                                error={!!errors.border_width}
                                                helperText={
                                                    errors.border_width?.message
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography color="text.secondary">
                                        {t("category.border_style")}
                                    </Typography>
                                    <Controller
                                        name="border_style"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth size="small">
                                                <Select {...field}>
                                                    {Object.values(
                                                        Enum_Border_Style,
                                                    ).map((value) => (
                                                        <MenuItem
                                                            key={value}
                                                            value={value}
                                                        >
                                                            {t(
                                                                `border_style.${value}`,
                                                            )}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={6}>
                            <Typography color="text.secondary">
                                {t("category.primary_color")}
                            </Typography>
                            <Controller
                                name="primary_color"
                                control={control}
                                render={({ field }) => (
                                    <ChromePicker
                                        color={field.value}
                                        onChangeComplete={(color) =>
                                            field.onChange(color.hex)
                                        }
                                    />
                                )}
                            />
                        </Grid>

                        {type === Enum_Geometry_Types.POLYGON && (
                            <Grid item xs={6}>
                                <Typography color="text.secondary">
                                    {t("category.background_color")}
                                </Typography>
                                <Controller
                                    name="background_color"
                                    control={control}
                                    render={({ field }) => (
                                        <ChromePicker
                                            color={field.value as string}
                                            onChangeComplete={(color) =>
                                                field.onChange(color.hex)
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        <Grid
                            item
                            xs={12}
                            display="flex"
                            justifyContent="flex-end"
                        >
                            <Button
                                type="submit"
                                size="large"
                                variant="contained"
                                color="primary"
                            >
                                {t("validate")}
                            </Button>
                        </Grid>
                    </Grid>
                </FormProvider>
            </Box>
        </Dialog>
    );
};

export default CreateCategory;
